// src/utils/oss.js
import OSS from "ali-oss";
import HomeApi from "@/api/homeApi.js";

const OSS_REGION = "cn-hangzhou";
const OSS_BUCKET = "ykwl-offical-oss";
const OSS_ENDPOINT = "oss-cn-hangzhou.aliyuncs.com";
const TOKEN_REFRESH_BUFFER_MS = 60 * 1000;

let globalClient = null;
let isInitializing = false;
let initPromise = null;
let cachedConfig = null;

export default class OssHelper {
  constructor() {
    this.preInit();
  }

  preInit() {
    if (!globalClient && !isInitializing && !initPromise) {
      this.startInit().catch((error) => {
        console.warn("OSS pre-initialization failed:", error);
      });
    }
  }

  startInit() {
    if (initPromise) return initPromise;

    isInitializing = true;
    initPromise = this._initOssClient()
      .catch((error) => {
        globalClient = null;
        cachedConfig = null;
        throw error;
      })
      .finally(() => {
        isInitializing = false;
        initPromise = null;
      });

    return initPromise;
  }

  clearClient() {
    globalClient = null;
    cachedConfig = null;
    initPromise = null;
    isInitializing = false;
  }

  async _initOssClient() {
    try {
      const ossConfig = await this.getOssCofig();

      globalClient = new OSS({
        accessKeyId: ossConfig.accessKey,
        accessKeySecret: ossConfig.secretKey,
        region: OSS_REGION,
        authorizationV4: true,
        bucket: OSS_BUCKET,
        endpoint: OSS_ENDPOINT,
        stsToken: ossConfig.securityToken,
        refreshSTSToken: async () => {
          const newConfig = await this.getOssCofig({ forceRefresh: true });
          return {
            accessKeyId: newConfig.accessKey,
            accessKeySecret: newConfig.secretKey,
            stsToken: newConfig.securityToken,
          };
        },
        refreshSTSTokenInterval: 5 * 60 * 1000,
      });

      return globalClient;
    } catch (error) {
      console.error("OSS initialization failed:", error);
      throw error;
    }
  }

  async getOssCofig(options = {}) {
    const { forceRefresh = false } = options;

    if (!forceRefresh && cachedConfig && this.notExpired(cachedConfig)) {
      return cachedConfig;
    }

    const res = await HomeApi.getOssInfo();
    this.validateConfig(res);

    cachedConfig = {
      accessKey: res.accessKey,
      secretKey: res.secretKey,
      securityToken: res.securityToken,
      expiration: new Date(res.expiration).getTime(),
    };

    return cachedConfig;
  }

  notExpired(config) {
    return Boolean(config?.expiration && config.expiration - TOKEN_REFRESH_BUFFER_MS > Date.now());
  }

  validateConfig(config) {
    const requiredFields = ["accessKey", "secretKey", "securityToken", "expiration"];

    requiredFields.forEach((field) => {
      if (!config?.[field]) throw new Error(`Missing required OSS config field: ${field}`);
    });

    if (Number.isNaN(new Date(config.expiration).getTime())) {
      throw new Error("Invalid OSS expiration time");
    }
  }

  async getClient(options = {}) {
    const { forceRefresh = false } = options;

    if (forceRefresh) {
      this.clearClient();
    }

    if (globalClient) {
      if (!cachedConfig || !this.notExpired(cachedConfig)) {
        return this.startInit();
      }
      return globalClient;
    }

    return this.startInit();
  }

  isOssUrl(url) {
    return /^https?:\/\//.test(url) && url.includes(OSS_ENDPOINT);
  }

  isExternalUrl(url) {
    return /^https?:\/\//.test(url);
  }

  normalizeObjectKey(objectKey) {
    if (!objectKey) return "";

    const value = String(objectKey).trim();
    if (!value || /^(data|blob):/.test(value)) return "";

    if (this.isExternalUrl(value)) {
      if (!this.isOssUrl(value)) return "";

      try {
        const parsedUrl = new URL(value);
        return decodeURIComponent(parsedUrl.pathname.replace(/^\/+/, ""));
      } catch (error) {
        console.warn("Invalid OSS url:", value, error);
        return "";
      }
    }

    return value;
  }

  async getAuthOssUrl(objectKey, expires = 600, process = "", options = {}) {
    const source = String(objectKey || "").trim();

    if (!source || /^(data|blob):/.test(source)) return source;
    if (this.isExternalUrl(source) && !this.isOssUrl(source)) return source;

    const normalizedObjectKey = this.normalizeObjectKey(source);
    if (!normalizedObjectKey) return source;

    try {
      const client = await this.getClient({ forceRefresh: options.forceRefresh });
      const signatureOptions = { expires };
      if (process) signatureOptions.process = process;

      const signedUrl = client.signatureUrl(normalizedObjectKey, signatureOptions);
      if (!signedUrl) throw new Error("Empty OSS signed URL");

      return signedUrl;
    } catch (error) {
      console.error("Failed to generate OSS signed URL:", error);
      throw error;
    }
  }

  isCredentialsExpired(credentials) {
    if (!credentials) return true;

    const expireDate = new Date(credentials.Expiration);
    return expireDate.getTime() - Date.now() <= TOKEN_REFRESH_BUFFER_MS;
  }
}

export const ossHelper = new OssHelper();
